export type LibraryWbsNode = {
  id: string;
  code: string;
  name?: string;
  parent_id?: string | null;
  layer_code?: string;
  sort_order?: number;
  description?: string;
  short_name?: string;
};

export function parentIdOf(node: LibraryWbsNode): string | null {
  return node.parent_id ? String(node.parent_id) : null;
}

export function siblingsOf(nodes: LibraryWbsNode[], parentId: string | null): LibraryWbsNode[] {
  return nodes
    .filter((n) => parentIdOf(n) === parentId)
    .sort(
      (a, b) =>
        (Number(a.sort_order) || 100) - (Number(b.sort_order) || 100) ||
        a.code.localeCompare(b.code)
    );
}

/** Pre-order walk respecting expanded set */
export function visiblePreorder(
  nodes: LibraryWbsNode[],
  expandedKeys: Set<string>
): LibraryWbsNode[] {
  const out: LibraryWbsNode[] = [];
  const walk = (parentId: string | null) => {
    for (const s of siblingsOf(nodes, parentId)) {
      out.push(s);
      if (expandedKeys.has(String(s.id))) walk(String(s.id));
    }
  };
  walk(null);
  return out;
}

export function filterNodesBySearch(nodes: LibraryWbsNode[], query: string): LibraryWbsNode[] {
  const q = query.trim().toLowerCase();
  if (!q) return nodes;
  const matchIds = new Set<string>();
  for (const n of nodes) {
    const hit =
      n.code.toLowerCase().includes(q) ||
      (n.name || '').toLowerCase().includes(q) ||
      (n.short_name || '').toLowerCase().includes(q);
    if (hit) {
      matchIds.add(String(n.id));
      let pid = parentIdOf(n);
      while (pid) {
        matchIds.add(pid);
        const p = nodes.find((x) => String(x.id) === pid);
        pid = p ? parentIdOf(p) : null;
      }
    }
  }
  return nodes.filter((n) => matchIds.has(String(n.id)));
}

export function computeSortOrders(
  orderedIds: string[],
  base = 10
): Array<{ id: string; sortOrder: number }> {
  return orderedIds.map((id, i) => ({ id, sortOrder: (i + 1) * base }));
}

export type TreeDropIntent =
  | { type: 'reparent'; dragId: string; newParentId: string }
  | { type: 'reorder'; dragId: string; parentId: string | null; beforeId?: string; afterId?: string };

export function resolveTreeDrop(
  nodes: LibraryWbsNode[],
  dragId: string,
  targetId: string,
  dropPosition: number
): TreeDropIntent | null {
  if (dragId === targetId) return null;
  const drag = nodes.find((n) => String(n.id) === dragId);
  const target = nodes.find((n) => String(n.id) === targetId);
  if (!drag || !target) return null;

  const isDescendant = (ancestorId: string, nodeId: string): boolean => {
    let cur = nodes.find((n) => String(n.id) === nodeId);
    while (cur) {
      const pid = parentIdOf(cur);
      if (!pid) return false;
      if (pid === ancestorId) return true;
      cur = nodes.find((n) => String(n.id) === pid);
    }
    return false;
  };
  if (isDescendant(dragId, targetId)) return null;

  if (dropPosition === 0) {
    return { type: 'reparent', dragId, newParentId: targetId };
  }

  const parentId = parentIdOf(target);
  const sibs = siblingsOf(nodes, parentId).map((s) => String(s.id));
  const targetIdx = sibs.indexOf(targetId);
  if (targetIdx < 0) return null;

  if (dropPosition < 0) {
    return {
      type: 'reorder',
      dragId,
      parentId,
      beforeId: targetId,
      afterId: targetIdx > 0 ? sibs[targetIdx - 1] : undefined,
    };
  }
  return {
    type: 'reorder',
    dragId,
    parentId,
    afterId: targetId,
    beforeId: targetIdx < sibs.length - 1 ? sibs[targetIdx + 1] : undefined,
  };
}

export function siblingOrderAfterMove(
  nodes: LibraryWbsNode[],
  dragId: string,
  parentId: string | null,
  insertBeforeId?: string
): string[] {
  const sibs = siblingsOf(nodes, parentId)
    .map((s) => String(s.id))
    .filter((id) => id !== dragId);
  if (insertBeforeId) {
    const idx = sibs.indexOf(insertBeforeId);
    if (idx >= 0) sibs.splice(idx, 0, dragId);
    else sibs.push(dragId);
  } else {
    sibs.push(dragId);
  }
  return sibs;
}
